import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ModernSidebar from "@/components/modern/ModernSidebar";
import ModernTopbar from "@/components/modern/ModernTopbar";
import MobileTabBar, { showsTabBar } from "@/components/modern/MobileTabBar";
import { X } from "lucide-react";
import useDialogFocus from "@/components/ui/useDialogFocus";
import { CompanyLogo } from "@/components/BrandLogo";

const KEY = "madio.nav.collapsed";
const read = () => { try { return window.localStorage.getItem(KEY) === "1"; } catch { return false; } };
// Below lg (phones, portrait tablets) the sidebar is a drawer and a bottom tab bar holds the daily pages.

/** The modern shell (App.js already renders the page's "Skip to main content" link): a top bar over a collapsible grouped
 * sidebar and the page. Below 1024px (phones, portrait tablets) the sidebar becomes a drawer and a bottom tab bar
 * (MobileTabBar) carries the person's most-used pages. The collapsed state is remembered per browser. */
export default function ModernShell({ children }) {
  const [collapsed, setCollapsed] = useState(read);
  const [drawer, setDrawer] = useState(false);
  const { pathname } = useLocation();
  const drawerRef = useDialogFocus(drawer, () => setDrawer(false));

  useEffect(() => { try { window.localStorage.setItem(KEY, collapsed ? "1" : "0"); } catch { /* optional */ } }, [collapsed]);
  useEffect(() => setDrawer(false), [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-bg)]" data-testid="modern-shell">
      <ModernTopbar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} onOpenMobile={() => setDrawer(true)} />
      <div className="flex-1 flex min-h-0">
        <div className="hidden lg:block shrink-0 sticky top-14 h-[calc(100vh-3.5rem)]"><ModernSidebar collapsed={collapsed} /></div>
        {drawer && (
          <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Navigation">
            <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close navigation" onClick={() => setDrawer(false)} tabIndex={-1} />
            <div ref={drawerRef} className="relative z-10 h-full max-w-[85vw] shadow-[var(--shadow-pop)]"><ModernSidebar onNavigate={() => setDrawer(false)} brand={(
              <div className="flex items-center justify-between px-4 h-14 border-b border-[var(--color-border)] shrink-0">
                <span className="flex items-center gap-3"><CompanyLogo className="h-5" /><span className="sr-only">Menu</span></span>
                <button type="button" onClick={() => setDrawer(false)} aria-label="Close navigation" className="inline-flex items-center justify-center w-10 h-10 -mr-2 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-muted)]"><X size={19} /></button>
              </div>
            )} /></div>
          </div>
        )}
        <main id="main-content" tabIndex={-1}
              className={`flex-1 min-w-0 outline-none overflow-x-hidden ${showsTabBar(pathname) ? "pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:pb-0" : ""}`}>{children}</main>
      </div>
      <MobileTabBar onMenu={() => setDrawer(true)} />
    </div>
  );
}
